import { FormEvent, useEffect, useState } from 'react';
import { RecipesApi, SalesApi } from '../api/resources';
import { ApiError } from '../api/client';
import type { Recipe, Sale, SalePaymentMethod } from '../api/types';
import { salePaymentMethodLabels } from '../api/types';
import { useToast } from '../context/ToastContext';
import { formatCOP, formatDateTime, formatQty } from '../utils/format';

interface CartItem {
  recipe: Recipe;
  quantity: number;
  unitCost: string;
  totalCost: string;
}

export function SaleRegistration() {
  const { notify } = useToast();
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [recipeId, setRecipeId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [adding, setAdding] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [clientDocument, setClientDocument] = useState('');
  const [clientName, setClientName] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<SalePaymentMethod>('EFECTIVO');
  const [saving, setSaving] = useState(false);
  const [sales, setSales] = useState<Sale[]>([]);
  const [loadingSales, setLoadingSales] = useState(true);

  async function loadSales() {
    setLoadingSales(true);
    try {
      const result = await SalesApi.list({ pageSize: 50 });
      setSales(result.items);
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible cargar las ventas.', 'error');
    } finally {
      setLoadingSales(false);
    }
  }

  useEffect(() => {
    RecipesApi.list({ isActive: true }).then((r) => setRecipes(r.recipes));
    void loadSales();
    // Carga inicial unica.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const total = cart.reduce((sum, item) => sum + Number(item.totalCost), 0);

  async function handleAdd(e: FormEvent) {
    e.preventDefault();
    const recipe = recipes.find((r) => r.id === recipeId);
    const qty = Number(quantity);
    if (!recipe || !(qty > 0)) {
      notify('Selecciona un producto y una cantidad mayor a cero.', 'error');
      return;
    }
    const existing = cart.find((item) => item.recipe.id === recipe.id);
    const newQuantity = (existing?.quantity ?? 0) + qty;
    setAdding(true);
    try {
      const { cost, totalProductionCost } = await RecipesApi.cost(recipe.id, newQuantity);
      const line: CartItem = {
        recipe,
        quantity: newQuantity,
        unitCost: String(cost.productionCost),
        totalCost: totalProductionCost,
      };
      setCart((current) =>
        existing ? current.map((item) => (item.recipe.id === recipe.id ? line : item)) : [...current, line],
      );
      setRecipeId('');
      setQuantity('1');
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible calcular el costo del producto.', 'error');
    } finally {
      setAdding(false);
    }
  }

  function removeItem(id: string) {
    setCart((current) => current.filter((item) => item.recipe.id !== id));
  }

  function cancelSale() {
    if (cart.length && !window.confirm('¿Cancelar la venta? Se quitarán todos los productos agregados.')) return;
    setCart([]);
    setRecipeId('');
    setQuantity('1');
    setShowConfirm(false);
    setClientDocument('');
    setClientName('');
    setPaymentMethod('EFECTIVO');
  }

  function closeConfirm() {
    if (!saving) setShowConfirm(false);
  }

  async function handleAccept(e: FormEvent) {
    e.preventDefault();
    if (!clientDocument.trim() || !clientName.trim()) {
      notify('El documento y el nombre del cliente son obligatorios.', 'error');
      return;
    }
    setSaving(true);
    try {
      await SalesApi.create({
        clientDocument: clientDocument.trim(),
        clientName: clientName.trim(),
        paymentMethod,
        items: cart.map((item) => ({ recipeId: item.recipe.id, quantity: item.quantity })),
      });
      notify('Venta registrada. El inventario fue actualizado.', 'success');
      setCart([]);
      setShowConfirm(false);
      setClientDocument('');
      setClientName('');
      setPaymentMethod('EFECTIVO');
      await loadSales();
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'No fue posible registrar la venta.', 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <h1>Registro de venta</h1>
      <p className="text-muted">Agrega uno o varios productos a la venta y regístrala; los insumos se descuentan del inventario automáticamente.</p>

      <div className="card">
        <h2>Agregar producto</h2>
        <form onSubmit={handleAdd} noValidate>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="saleRecipe">Producto (arreglo)</label>
              <select id="saleRecipe" value={recipeId} onChange={(e) => setRecipeId(e.target.value)}>
                <option value="">Selecciona un producto...</option>
                {recipes.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.code} - {r.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="saleQty">Cantidad</label>
              <input id="saleQty" type="number" min={1} step="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={adding || !recipeId}>
            {adding ? 'Agregando...' : 'Agregar a esta venta'}
          </button>
        </form>
      </div>

      <div className="card">
        <h2>Esta venta</h2>
        {cart.length === 0 ? (
          <p className="text-muted">Aún no has agregado productos a esta venta.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Descripción</th>
                  <th className="text-right">Cantidad</th>
                  <th className="text-right">Costo unitario</th>
                  <th className="text-right">Costo</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {cart.map((item) => (
                  <tr key={item.recipe.id}>
                    <td>
                      <strong>{item.recipe.name}</strong>
                      <br />
                      <small className="text-muted">{item.recipe.code}</small>
                    </td>
                    <td>{item.recipe.description || '—'}</td>
                    <td className="text-right">{formatQty(item.quantity)}</td>
                    <td className="text-right">{formatCOP(item.unitCost)}</td>
                    <td className="text-right">{formatCOP(item.totalCost)}</td>
                    <td>
                      <button type="button" className="btn btn-danger btn-sm" onClick={() => removeItem(item.recipe.id)}>
                        Quitar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 700 }}>
                  <td colSpan={4} className="text-right">Total de la venta</td>
                  <td className="text-right">{formatCOP(total)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        <div className="actions-row mt-1">
          <button type="button" className="btn btn-primary" disabled={!cart.length} onClick={() => setShowConfirm(true)}>
            Registrar venta
          </button>
          <button type="button" className="btn btn-secondary" disabled={!cart.length} onClick={cancelSale}>
            Cancelar venta
          </button>
        </div>
      </div>

      <div className="card">
        <h2>Ventas registradas</h2>
        {loadingSales ? (
          <div className="loading-block">Cargando ventas...</div>
        ) : sales.length === 0 ? (
          <p className="text-muted">Aún no se han registrado ventas.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Documento</th>
                  <th>Cliente</th>
                  <th>Productos</th>
                  <th>Medio de pago</th>
                  <th className="text-right">Total</th>
                  <th>Registrado por</th>
                </tr>
              </thead>
              <tbody>
                {sales.map((sale) => (
                  <tr key={sale.id}>
                    <td>{formatDateTime(sale.createdAt)}</td>
                    <td>{sale.clientDocument}</td>
                    <td>{sale.clientName}</td>
                    <td>
                      {sale.items.map((item) => (
                        <div key={item.id}>
                          {item.recipeNameSnapshot} &times; {formatQty(item.quantity)}
                        </div>
                      ))}
                    </td>
                    <td>{salePaymentMethodLabels[sale.paymentMethod] ?? sale.paymentMethod}</td>
                    <td className="text-right">{formatCOP(sale.totalCost)}</td>
                    <td>{sale.user?.fullName ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showConfirm && (
        <div
          className="platform-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeConfirm();
          }}
        >
          <section className="platform-modal" role="dialog" aria-modal="true" aria-labelledby="sale-confirm-title">
            <div className="platform-modal-header">
              <div>
                <h2 id="sale-confirm-title">Registrar venta</h2>
                <p>
                  Fecha de venta: automática al aceptar &middot; Total {formatCOP(total)}
                </p>
              </div>
            </div>
            <form className="platform-form" onSubmit={handleAccept}>
              <div className="form-grid">
                <div className="field">
                  <label htmlFor="saleClientDoc">Documento del cliente</label>
                  <input id="saleClientDoc" required autoFocus value={clientDocument} onChange={(e) => setClientDocument(e.target.value)} />
                </div>
                <div className="field">
                  <label htmlFor="saleClientName">Nombre del cliente</label>
                  <input id="saleClientName" required value={clientName} onChange={(e) => setClientName(e.target.value)} />
                </div>
                <div className="field">
                  <label htmlFor="salePaymentMethod">Medio de pago</label>
                  <select id="salePaymentMethod" required value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value as SalePaymentMethod)}>
                    {Object.entries(salePaymentMethodLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="actions-row platform-modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeConfirm} disabled={saving}>
                  Volver
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Registrando...' : 'Aceptar'}
                </button>
              </div>
            </form>
          </section>
        </div>
      )}
    </div>
  );
}
